import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { IconComponent, TypographyComponent } from 'components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	ChartWidget,
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	hasPositiveTotal,
	platformDisplayName
} from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import {
	GetSocialListeningMyBrandAnalyticsMentionsByMediaTypeService,
	MentionsByMediaTypePlatform
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-mentions-by-media-type.service';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

// Categorical channel palette — one color per channel row, in order, mirroring
// the LOLLY report mockup (Figma 13302:101781). Cycles if there are >8 rows.
const PLATFORM_COLORS = [
	'#2872e1',
	'#e94f6a',
	'#f0a3c6',
	'#a98cf0',
	'#6cd6c8',
	'#f5c451',
	'#9aa3bb',
	'#5b8def'
];

interface MediaTypeRow {
	label: string;
	color: string;
	value: number;
	percent: string;
}

@Component({
	standalone: true,
	selector: 'app-mentions-by-media-type-slide',
	templateUrl: './mentions-by-media-type-slide.component.html',
	styleUrls: ['./mentions-by-media-type-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		IconComponent,
		TypographyComponent,
		NgChartsModule,
		SlideNoDataComponent,
		DecimalPipe,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class MentionsByMediaTypeSlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	titleColor = '#2872e1';
	dateLabel = '';
	summaryText = '';
	topSharePct = '';
	topShareLabel = '';
	tableRows: MediaTypeRow[] = [];
	chartData: ChartConfiguration<'doughnut'>['data'] | null = null;

	readonly chartOptions: ChartConfiguration<'doughnut'>['options'] = {
		responsive: true,
		maintainAspectRatio: false,
		animation: false,
		cutout: '65%',
		plugins: { legend: { display: false } }
	};
	readonly chartType = 'doughnut' as const;
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'mentions_by_media_type',
		null,
		null
	);

	private readonly destroy$ = new Subject<void>();
	private cachedPlatforms: MentionsByMediaTypePlatform[] | null = null;
	private cacheKey: string | null = null;

	get isEmpty(): boolean {
		return (
			!this.cachedPlatforms ||
			!hasPositiveTotal(
				this.cachedPlatforms.map(platform => platform.count)
			)
		);
	}

	constructor(
		private readonly analyticsService: GetSocialListeningMyBrandAnalyticsMentionsByMediaTypeService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedPlatforms && this.cacheKey === currentCacheKey) {
			this.applyPlatforms(this.cachedPlatforms);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(platforms => {
				this.cachedPlatforms = platforms;
				this.cacheKey = currentCacheKey;
				this.applyPlatforms(platforms);
			});
	}

	private applyPlatforms(platforms: MentionsByMediaTypePlatform[]): void {
		const rawSlide = this.buildSlide(platforms);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide, platforms);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(platforms: MentionsByMediaTypePlatform[]): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;

		const chartWidget: ChartWidget = {
			id: `${prefix}_chart`,
			kind: 'chart',
			visible: true,
			content: {
				chartType: 'doughnut',
				dataSourceKey: 'mentions_by_media_type',
				seriesMeta: platforms.map((platform, index) => ({
					key: platform.platform.toLowerCase(),
					label: platformDisplayName(platform.platform),
					color: PLATFORM_COLORS[index % PLATFORM_COLORS.length]
				})),
				chartJsConfig: {
					type: 'doughnut',
					data: {
						labels: platforms.map(platform =>
							platformDisplayName(platform.platform)
						),
						datasets: [
							{
								data: platforms.map(platform => platform.count),
								backgroundColor: platforms.map(
									(_, index) =>
										PLATFORM_COLORS[
											index % PLATFORM_COLORS.length
										]
								),
								borderWidth: 0
							}
						]
					}
				}
			}
		};

		return {
			id: `${prefix}_slide`,
			slideType: 'mentions_by_media_type',
			title: this.item.title || 'Mentions by Media Type',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Mentions by Media Type',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				chartWidget
			]
		};
	}

	private updateViewModel(
		slide: PreviewSlide,
		platforms: MentionsByMediaTypePlatform[]
	): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			widget => widget.kind === 'title' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			widget => widget.kind === 'date_range' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const summaryWidget = slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';

		const total = platforms.reduce((sum, p) => sum + p.count, 0);

		const chartWidget = slide.widgets.find(
			widget => widget.kind === 'chart' && !hidden.has(widget.id)
		) as ChartWidget | undefined;

		const resolvedColors =
			chartWidget?.content.seriesMeta?.map(
				(series, index) =>
					series.color ??
					PLATFORM_COLORS[index % PLATFORM_COLORS.length]
			) ??
			platforms.map(
				(_, index) => PLATFORM_COLORS[index % PLATFORM_COLORS.length]
			);

		this.chartData = {
			labels: platforms.map(p => platformDisplayName(p.platform)),
			datasets: [
				{
					data: platforms.map(p => p.count),
					backgroundColor: resolvedColors,
					borderWidth: 0
				}
			]
		};

		this.tableRows = platforms.map((platform, index) => ({
			label: platformDisplayName(platform.platform),
			color:
				resolvedColors[index] ??
				PLATFORM_COLORS[index % PLATFORM_COLORS.length],
			value: platform.count,
			percent:
				total > 0
					? `${((platform.count / total) * 100).toFixed(1)}%`
					: '0%'
		}));

		// KPI callout — the leading channel's share of total mention volume.
		const top = platforms.reduce(
			(best, p) => (p.count > best.count ? p : best),
			{ platform: '', count: 0 } as MentionsByMediaTypePlatform
		);
		if (total > 0 && top.count > 0) {
			this.topSharePct = `${((top.count / total) * 100).toFixed(1)}%`;
			this.topShareLabel = `${platformDisplayName(top.platform)} share`;
		} else {
			this.topSharePct = '';
			this.topShareLabel = '';
		}

		// A channel per label, in the colour the breakdown beside it uses.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				labels: this.tableRows.map(row => row.label),
				series: [
					{
						key: 'mentions',
						label: 'Mentions',
						values: this.tableRows.map(row => row.value)
					}
				],
				labelColors: this.tableRows.map(row => row.color),
				labelColumn: 'Channel'
			})
		);
	}
}
